'use client';

import { Button } from '@/components/ui/button/button';
import { Image } from '@/components/ui/image/image';
import { cn } from '@/lib/utils';
import NextLink from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import styles from './post-card.module.scss';

export type PostCardProps = {
  image?: string | null;
  imageAlt?: string | null;
  tags?: string[];
  resourceType?: string | null;
  title?: string | null;
  description?: string | null;
  date?: string | null;
  minRead?: number | null;
  ctaLabel?: string | null;
  ctaUrl?: string | null;
  className?: string;
};

const formatDate = (dateStr: string): string =>
  new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

export const PostCard = ({
  image,
  imageAlt,
  tags,
  resourceType,
  title,
  description,
  date,
  minRead,
  ctaLabel,
  ctaUrl,
  className,
}: PostCardProps) => {
  const router = useRouter();
  const [loading, setLoading] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    if (!ctaUrl) return;
    e.preventDefault();
    setLoading(true);
    router.push(ctaUrl);
  };

  return (
    <article className={cn(styles.card, className)}>
      <div className={styles.card__image_wrap}>
        {image && (
          <Image
            src={image}
            alt={imageAlt ?? ''}
            fill
            className={styles.card__image}
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
          />
        )}
      </div>

      <div className={styles.card__body}>
        <div className={styles.card__content}>
          {(resourceType || tags?.length || date || minRead != null) && (
            <div className={styles.card__tags}>
              {resourceType ? (
                <span className={cn(styles.card__tag, styles['card__tag--type'])}>
                  {resourceType}
                </span>
              ) : (
                tags?.map((t) => (
                  <span key={t} className={styles.card__tag}>
                    {t}
                  </span>
                ))
              )}
              {date && (
                <span className={cn(styles.card__tag, styles['card__tag--date'])}>
                  {formatDate(date)}
                </span>
              )}
              {minRead != null && (
                <span className={cn(styles.card__tag, styles['card__tag--min-read'])}>
                  {minRead} min read
                </span>
              )}
            </div>
          )}
          <div className={styles.card__text}>
            {title && <h3 className={styles.card__title}>{title}</h3>}
            {description && <p className={styles.card__description}>{description}</p>}
          </div>
        </div>

        {ctaLabel && ctaUrl && (
          <div className={styles.card__cta_wrap}>
            <NextLink href={ctaUrl} onClick={handleClick}>
              <Button withArrow={!loading} className={styles.cta} tabIndex={-1}>
                <span className={cn(styles.ctaInner, loading && styles.ctaLoading)}>
                  <span className={styles.ctaText}>{ctaLabel}</span>
                  {loading && <span className={styles.spinner} />}
                </span>
              </Button>
            </NextLink>
          </div>
        )}
      </div>
    </article>
  );
};
